import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';

// Render Strapi 5 "blocks" answer as plain HTML
function Answer({ blocks }: { blocks: any }) {
  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) {
    return <p className="text-sm text-gray-400">No answer.</p>;
  }
  const inline = (children: any[]): React.ReactNode =>
    (children || []).map((c, i) => {
      const text = c?.text || '';
      let node: React.ReactNode = text;
      if (c?.bold) node = <strong key={i}>{node}</strong>;
      if (c?.italic) node = <em key={i}>{node}</em>;
      if (c?.code) node = <code key={i} className="bg-gray-100 rounded px-1 text-xs">{node}</code>;
      if (c?.underline) node = <u key={i}>{node}</u>;
      if (c?.type === 'link' && c?.url) return <a key={i} href={c.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{inline(c.children)}</a>;
      return <span key={i}>{node}</span>;
    });

  return (
    <div className="space-y-3 text-gray-700 leading-relaxed">
      {blocks.map((b: any, i: number) => {
        if (b.type === 'heading') {
          const level = Math.min(Math.max(Number(b.level) || 2, 1), 6);
          const Tag = (`h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6');
          return <Tag key={i} className="font-bold text-gray-900 mt-4">{inline(b.children)}</Tag>;
        }
        if (b.type === 'list') {
          const Tag = b.format === 'ordered' ? 'ol' : 'ul';
          const cls = Tag === 'ol' ? 'list-decimal pl-6 space-y-1' : 'list-disc pl-6 space-y-1';
          return (
            <Tag key={i} className={cls}>
              {(b.children || []).map((li: any, j: number) => (
                <li key={j}>{inline(li.children)}</li>
              ))}
            </Tag>
          );
        }
        if (b.type === 'quote') {
          return <blockquote key={i} className="border-l-4 border-primary bg-primary/5 pl-4 py-2 italic text-gray-700">{inline(b.children)}</blockquote>;
        }
        if (b.type === 'code') {
          return <pre key={i} className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-auto">{(b.children || []).map((c: any) => c?.text || '').join('')}</pre>;
        }
        // paragraph (default)
        return <p key={i}>{inline(b.children)}</p>;
      })}
    </div>
  );
}

export default function FaqPreviewPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const [params] = useSearchParams();
  const secret = params.get('secret') || '';
  const status = (params.get('status') === 'draft' ? 'draft' : 'published') as 'draft' | 'published';

  const [faq, setFaq] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId || !secret) {
      setError('Missing preview parameters.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/api/faqs/preview/${documentId}`, { params: { secret, status } })
      .then((res) => setFaq(res.data?.data || null))
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load preview'))
      .finally(() => setLoading(false));
  }, [documentId, secret, status]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>FAQ preview — {faq?.question || documentId}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className={`w-full px-4 py-2 text-center text-xs font-semibold ${
        status === 'draft'
          ? 'bg-amber-100 text-amber-800 border-b border-amber-200'
          : 'bg-green-100 text-green-800 border-b border-green-200'
      }`}>
        {status === 'draft' ? '⚠ Preview · DRAFT (unpublished)' : '✓ Preview · Published version'}
      </div>

      {loading ? (
        <div className="max-w-2xl mx-auto px-4 py-10">
          <div className="h-8 bg-gray-200 rounded animate-pulse mb-4 w-3/4" />
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded animate-pulse" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-5/6" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-4/6" />
          </div>
        </div>
      ) : error ? (
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h1 className="font-bold text-gray-900 text-lg mb-2">Preview unavailable</h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      ) : faq ? (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="inline-block text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 rounded-full px-2.5 py-1 mb-4">
              FAQ
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight mb-5">
              {faq.question || '(untitled)'}
            </h1>
            <Answer blocks={faq.answer} />
          </div>

          <div className="text-center text-[11px] text-gray-400 py-4 mt-2">
            Preview · documentId <code>{documentId}</code> · status <strong className="uppercase">{status}</strong>
          </div>
        </div>
      ) : null}
    </div>
  );
}
